import { useEffect, useRef, useState } from 'react';
import {
  createInstitutionalRule,
  createInstitutionalRuleVersion,
  getInstitutionalConcepts,
  getInstitutionalActs,
  type InstitutionalRuleInput,
  type InstitutionalRuleRecord,
  type ReviewOrganization,
} from '@urbanik/api-client';
import { Button, CanonicalModal, SelectField, TextField, TextAreaField } from '@urbanik/ui';
import { normalizeRuleAmount } from './institutional-rule-amount';
import { reviewApiBase } from './api-base';
export function InstitutionalRuleEditor({
  organization,
  onClose,
  onSaved,
  rule = null,
}: {
  organization: ReviewOrganization;
  onClose: () => void;
  onSaved: () => void;
  rule?: InstitutionalRuleRecord | null;
}) {
  const [concepts, setConcepts] = useState<
    Awaited<ReturnType<typeof getInstitutionalConcepts>>['items']
  >([]);
  const [acts, setActs] = useState<Awaited<ReturnType<typeof getInstitutionalActs>>['items']>([]);
  const [loading, setLoading] = useState(true),
    [saving, setSaving] = useState(false),
    [error, setError] = useState(''),
    [reload, setReload] = useState(0);
  const [draft, setDraft] = useState<InstitutionalRuleInput>(
    rule
      ? {
          conceptId: rule.conceptId,
          actId: rule.actId,
          amount: rule.amount,
          periodicity: rule.periodicity as InstitutionalRuleInput['periodicity'],
          effectiveFrom: rule.effectiveFrom,
          effectiveTo: rule.effectiveTo,
          condition: rule.condition,
        }
      : {
          conceptId: '',
          actId: '',
          amount: '',
          periodicity: 'monthly',
          effectiveFrom: '',
          effectiveTo: null,
          condition: '',
        },
  );
  const errorRef = useRef<HTMLParagraphElement | null>(null);
  useEffect(() => {
    if (error) errorRef.current?.focus();
  }, [error]);
  const pending = useRef<AbortController | null>(null),
    inFlight = useRef(false);
  const allowed = [
    'governance.rules.create',
    'governance.concepts.read',
    'governance.acts.read',
  ].every((p) => organization.permissions.includes(p));
  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError('');
    setConcepts([]);
    setActs([]);
    if (!allowed) {
      setLoading(false);
      return () => controller.abort();
    }
    void Promise.all([
      getInstitutionalConcepts(reviewApiBase, organization.id, controller.signal),
      getInstitutionalActs(reviewApiBase, organization.id, controller.signal),
    ])
      .then(([c, a]) => {
        if (!controller.signal.aborted) {
          setConcepts(c.items.filter((i) => i.active));
          setActs(
            a.items.filter((i) => !i.deletedAt && i.documentStatus === 'approved' && i.hasDocument),
          );
        }
      })
      .catch((e: unknown) => {
        if (!controller.signal.aborted)
          setError(e instanceof Error ? e.message : 'No se pudieron cargar las referencias.');
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => {
      controller.abort();
      pending.current?.abort();
    };
  }, [organization.id, allowed, reload]);
  async function save() {
    if (
      !allowed ||
      loading ||
      inFlight.current ||
      !concepts.some((c) => c.id === draft.conceptId) ||
      !acts.some((a) => a.id === draft.actId)
    )
      return;
    const controller = new AbortController();
    pending.current = controller;
    inFlight.current = true;
    setSaving(true);
    setError('');
    try {
      const input = { ...draft, amount: normalizeRuleAmount(draft.amount) };
      if (rule)
        await createInstitutionalRuleVersion(
          reviewApiBase,
          organization.id,
          rule.id,
          { ...input, expectedVersionNumber: rule.versionNumber },
          controller.signal,
        );
      else await createInstitutionalRule(reviewApiBase, organization.id, input, controller.signal);
      if (!controller.signal.aborted) onSaved();
    } catch (e) {
      if (!controller.signal.aborted)
        setError(e instanceof Error ? e.message : 'No se pudo guardar.');
    } finally {
      inFlight.current = false;
      if (!controller.signal.aborted) setSaving(false);
    }
  }
  return (
    <CanonicalModal
      open
      title={rule ? 'Nueva versión de regla' : 'Nueva regla de importe fijo'}
      description={organization.name + ' · ' + organization.code}
      onClose={() => {
        if (!saving) onClose();
      }}
    >
      {!allowed ? (
        <p role="alert">
          Se requieren permisos de creación de reglas y consulta de conceptos y actas.
        </p>
      ) : (
        <form
          className="concepts-form"
          onSubmit={(e) => {
            e.preventDefault();
            void save();
          }}
        >
          {error ? (
            <p role="alert" tabIndex={-1} ref={errorRef}>
              {error}
            </p>
          ) : null}
          {loading ? <p role="status">Cargando conceptos y actas de esta entidad…</p> : null}
          {!loading && (!concepts.length || !acts.length) ? (
            <p>
              No hay referencias disponibles: necesitas un concepto activo y un acta con PDF
              aprobado.
            </p>
          ) : null}
          <SelectField
            label="Concepto activo"
            value={draft.conceptId}
            disabled={loading || saving}
            options={[
              { value: '', label: 'Selecciona un concepto' },
              ...concepts.map((c) => ({ value: c.id, label: c.code + ' · ' + c.name })),
            ]}
            onValueChange={(conceptId) => setDraft({ ...draft, conceptId })}
          />
          <SelectField
            label="Acta de respaldo"
            value={draft.actId}
            disabled={loading || saving}
            options={[
              { value: '', label: 'Selecciona un acta aprobada' },
              ...acts.map((a) => ({ value: a.id, label: a.number + ' · ' + a.subject })),
            ]}
            onValueChange={(actId) => setDraft({ ...draft, actId })}
          />
          <TextField
            label="Importe fijo USD"
            required
            inputMode="decimal"
            placeholder="5.00"
            value={draft.amount}
            disabled={saving}
            onChange={(e) => setDraft({ ...draft, amount: e.target.value })}
          />
          <p>Importe sin separadores de miles: puedes escribir 5, 5.50 o 5,50.</p>
          <SelectField
            label="Periodicidad"
            value={draft.periodicity}
            disabled={saving}
            options={[
              { value: 'once', label: 'Una vez' },
              { value: 'monthly', label: 'Mensual' },
              { value: 'quarterly', label: 'Trimestral' },
              { value: 'semiannual', label: 'Semestral' },
              { value: 'annual', label: 'Anual' },
            ]}
            onValueChange={(periodicity) =>
              setDraft({
                ...draft,
                periodicity: periodicity as InstitutionalRuleInput['periodicity'],
              })
            }
          />
          <TextField
            label="Inicio de vigencia"
            type="date"
            required
            value={draft.effectiveFrom}
            disabled={saving}
            onChange={(e) => setDraft({ ...draft, effectiveFrom: e.target.value })}
          />
          <TextField
            label="Fin de vigencia (opcional)"
            type="date"
            value={draft.effectiveTo ?? ''}
            disabled={saving}
            onChange={(e) => setDraft({ ...draft, effectiveTo: e.target.value || null })}
          />
          <TextAreaField
            label="Condición de aplicación"
            required
            minLength={3}
            maxLength={500}
            value={draft.condition}
            disabled={saving}
            onChange={(e) => setDraft({ ...draft, condition: e.target.value })}
          />
          <p>
            El código se genera por entidad. Guardar crea una fila inmutable nueva; no genera
            obligaciones ni pagos.
          </p>
          <Button
            type="button"
            disabled={loading || saving}
            onClick={() => setReload((v) => v + 1)}
          >
            Actualizar referencias
          </Button>
          <Button type="button" disabled={saving} onClick={onClose}>
            Cancelar
          </Button>
          <Button type="submit" disabled={loading || saving || !draft.conceptId || !draft.actId}>
            {saving ? 'Guardando…' : rule ? 'Guardar nueva versión' : 'Guardar regla'}
          </Button>
        </form>
      )}
    </CanonicalModal>
  );
}
